import { HTMLAttributes, ReactNode, useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";

export function List({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn("flex flex-col gap-2", className)} {...props}>
      {children}
    </div>
  );
}

export function AccordionContainer({
  children,
  type = "multiple",
  collapsed,
}: {
  children: ReactNode;
  type?: "multiple" | "single";
  collapsed?: boolean;
}) {
  return (
    <Accordion
      type={type}
      className={cn(
        "transition-all duration-300",
        collapsed ? "w-[60px]" : "w-[250px]"
      )}
    >
      {children}
    </Accordion>
  );
}

export function AccordionList({
  children,
  value = crypto.randomUUID(),
  title,
  icon,
  collapsed,
}: {
  children: ReactNode;
  title: string;
  value?: string;
  icon?: ReactNode;
  collapsed?: boolean;
}) {
  const memoizedValue = useMemo(() => value, [value]);

  return (
    <AccordionItem value={memoizedValue} className="border-b-0">
      <AccordionTrigger className="flex items-center gap-3 px-2 rounded-lg text-white hover:bg-white hover:text-primary">
        {icon && <span className="text-xl">{icon}</span>}
        <span
          className={cn(
            "transition-all duration-300",
            collapsed ? "opacity-0 w-0" : "opacity-100"
          )}
        >
          {title}
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <List>{children}</List>
      </AccordionContent>
    </AccordionItem>
  );
}

export function NavTitle({
  children,
  collapsed,
}: {
  children: ReactNode;
  collapsed?: boolean;
}) {
  return (
    <div className="mt-7 first:mt-0">
      <h3
        className={cn(
          "font-medium transition-all duration-300",
          `${colors.text_light}`,
          "dark:text-[#F4F4F5]",
          collapsed ? "opacity-0 w-0" : "opacity-100"
        )}
      >
        {children}
      </h3>
      <hr className="mt-2" />
    </div>
  );
}

export function NavLink({
  children: [icon, ...children],
  to,
  refs = [],
  disabled = false,
  collapsed = false,
}: {
  children: ReactNode[];
  to: string;
  refs?: string[];
  disabled?: boolean;
  collapsed?: boolean;
}) {
  const { pathname } = useLocation();
  const highlight =
  pathname.startsWith(to) || refs.find((value) => pathname === value);

  return (
    <Link
      to={to}
      className={cn(
        "flex items-center gap-3 p-2 rounded-s-lg text-white hover:bg-white hover:shadow hover:text-primary ease-in duration-100",
        colors.text_light,
        `data-[state=highlight]:bg-[#F4F4F5] data-[state=highlight]:text-primary data-[state=highlight]:border-r-4 data-[state=highlight]:border-[#FF8500] data-[state=highlight]:shadow data-[state=highlight]:font-medium`,
        "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60 dark:text-[#F4F4F5]",
        colors.dark_navLink,
        collapsed ? "justify-center" : ""
      )}
      data-state={highlight ? "highlight" : "default"}
      data-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
    >
      <span
        className="text-current duration-300"
        data-state={highlight ? "highlight" : "default"}
      >
        {icon}
      </span>
      <span
        className={cn(
          "transition-all duration-300",
          collapsed ? "hidden" : "opacity-100"
        )}
      >
        {children}
      </span>
    </Link>
  );
}

interface NavCollapseProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsed?: boolean;
}

export function NavCollapse({ title, icon, children, collapsed }: NavCollapseProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center w-full px-2 py-2 text-sm font-medium rounded-s-lg text-white hover:bg-white dark:hover:bg-card/70 hover:shadow hover:text-primary ease-in duration-100",
          colors.text_light,
          "dark:text-[#F4F4F5]",
          isOpen && "bg-white dark:bg-card text-primary shadow font-medium",
          collapsed ? "justify-center" : ""
        )}
      >
        <span className="flex items-center gap-3">
          <span className="text-current duration-300">{icon}</span>
          {!collapsed && (
            <>
              <span>{title}</span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  isOpen && "transform rotate-180"
                )}
              />
            </>
          )}
        </span>
      </button>

      <div
        className={cn(
          "transition-all duration-200",
          isOpen ? "max-h-[500px] overflow-y-auto mt-2" : "max-h-0 overflow-hidden"
        )}
      >
        <div className="flex flex-col gap-2 pl-2">
          {children}
        </div>
      </div>
    </div>
  );
}

interface NavLinkWithSubmenuProps {
  title: string;
  to: string;
  refs?: string[];
  icon: React.ReactNode;
  children: React.ReactNode;
  collapsed?: boolean;
}

export function NavLinkWithSubmenu({ title, to, refs = [], icon, children, collapsed }: NavLinkWithSubmenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();
  const highlight = pathname.startsWith(to) || refs.find((value) => pathname === value);

  return (
    <div>
      <div
        className={cn(
          "flex items-center w-full rounded-s-lg text-white hover:bg-white hover:shadow hover:text-primary ease-in duration-100",
          colors.text_light,
          "dark:text-[#F4F4F5]",
          highlight && "bg-[#F4F4F5] dark:bg-card text-primary border-r-4 border-[#FF8500] shadow font-medium",
          colors.dark_navLink,
          collapsed ? "justify-center" : ""
        )}
      >
        <Link
          to={to}
          className={cn(
            "flex items-center gap-3 p-2 flex-1",
            collapsed ? "justify-center" : ""
          )}
        >
          <span className="text-current duration-300">{icon}</span>
          {!collapsed && <span>{title}</span>}
        </Link>
        {!collapsed && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className="p-2 hover:bg-primary/10 rounded-r-lg"
          >
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isOpen && "transform rotate-180"
              )}
            />
          </button>
        )}
      </div>

      <div
        className={cn(
          "overflow-hidden transition-all duration-200",
          isOpen ? "max-h-[800px] mt-2" : "max-h-0"
        )}
      >
        <div className="flex flex-col gap-1 pl-6 border-l-2 border-primary/20 ml-4">
          {children}
        </div>
      </div>
    </div>
  );
}
